import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const useMetaPixel = () => {
  const location = useLocation();
  const didSkipInitialPageViewRef = useRef(false);
  const lastTrackedUrlRef = useRef(null);

  useEffect(() => {
    const url = `${location.pathname}${location.search}`;

    // Avoid duplicate tracks for the same URL (React 18 StrictMode can re-run effects in dev).
    if (lastTrackedUrlRef.current === url) return;

    // The snippet in `public/index.html` already fires an initial `PageView`.
    // We skip the first tracked route so we don't double count.
    if (!didSkipInitialPageViewRef.current) {
      didSkipInitialPageViewRef.current = true;
      lastTrackedUrlRef.current = url;
      return;
    }

    let cancelled = false;

    const tryTrack = () => {
      if (cancelled) return false;
      if (typeof window === 'undefined') return false;
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'PageView');
        lastTrackedUrlRef.current = url;
        return true;
      }
      return false;
    };

    if (tryTrack()) return;

    // If the pixel script hasn't finished loading yet, retry briefly.
    const intervalId = window.setInterval(() => {
      if (tryTrack()) window.clearInterval(intervalId);
    }, 50);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname, location.search]);
};

